import hideFocusOnMouseClick from "@lexus.com/ui/dist/packages/hideFocusOnMouseClick";
import "@lexus.com/ui/dist/packages/hideFocusOnMouseClick/index.styles.css";
import { PersonalizationProvider } from "@lexus.com/ui/dist/packages/personalization";
import { Provider } from "@lexus.com/ui/dist/packages/redux";
import { ConfigProvider, FeatureFlagProvider } from "@lexus.com/ui/dist/_nextgen/packages/config";
import ScrollPaddingTop from "@lexus.com/ui/dist/_nextgen/components/ScrollPaddingTop";
import { ThemeProvider } from "@lexus.com/ui/dist/_nextgen/packages/styled-component-themes";
import { createStore } from '@lexus.com/ui/dist/packages/redux';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { cloneElement, useEffect } from 'react';
import { get } from 'lodash-es';
import { useBreakpointName } from '@lexus.com/ui/dist/components/breakpoints';
import {
  withAnalytics,
  useAnalytics,
} from '@lexus.com/ui/dist/components/analytics';

import GlobalFooter from "@lexus.com/ui/dist/modules/GlobalFooter";
import CopyBlock from "@lexus.com/ui/dist/components/CopyBlock";
import { Icon } from "@lexus.com/ui/dist/_nextgen/components/Icon";
import PageNavigation from "@lexus.com/ui/dist/components/PageNavigation";
import ThirdPartyInterstitial from "@lexus.com/ui/dist/modules/ThirdPartyInterstitial";
import logo from "./logo.svg";

import "normalize.css";
import "./App.css";

const store = createStore({
  middleware: []
});

const queryClient = new QueryClient();

hideFocusOnMouseClick(); //Todo: Convert to component?

const CMSData = ({ children, path, transform, debug = false }) => {
  const { data: fullData, isLoading, error } = useQuery({
    queryKey: ['cmsData'],
    queryFn: async () => {
      const response = await axios.get('/.model.json');
      return response.data;
    },
    enabled: true,
  });
  const rawData = path ? get(fullData, `:items.root.:items.${path}`) : fullData;
  const data = transform ? transform(rawData) : rawData;

  if (isLoading || error) return children;

  return <>
    {debug && <pre>{JSON.stringify(data, null, 2)}</pre>}
    {cloneElement(children, data)}
  </>;
};

const transformHeader = (data) => {
    return {
        ...data,
        links: data?.links?.map(link => ({
            ...link,
            href: link.href?.startsWith('#') ? `/${link.href}` : link.href
        })) ?? []
    };
};

const transformFooter = ({
  footerId,
  primaryLinks = [],
  featuredLinks = [],
  socialMediaIcons,
  footerDisclaimer,
  footerCategories = [],
  footerCopyright,
} = {}) => ({
  id: footerId,
  featuredLinks,
  categories: footerCategories?.map(({ ctaListTitle, ctaListItems = [] }) => ({
    title: ctaListTitle,
    links: ctaListItems,
  })),
  links: primaryLinks,
  social: socialMediaIcons,
  disclaimer: footerDisclaimer,
  copyright: footerCopyright,
})

const theme= "gr";

const copy = ({
    404: "<h2>ERROR</h2><p>THE PAGE YOU REQUESTED CAN'T BE FOUND.</p>",
    500: "<h2>ERROR</h2><p>DUE TO TECHNICAL DIFFICULTIES, THIS PAGE IS NOT AVAILABLE. PLEASE CHECK BACK SOON AS WE'RE WORKING TO ADDRESS THE ISSUE.</p>",
})[process.env.REACT_APP_ERROR_CODE ?? 404];

const App = () => {
    const breakpoint = useBreakpointName();
    const fireAnalytics = useAnalytics('App', { breakpoint});

    useEffect(() => {
      document.body.dataset.pageTheme = theme;
      
      return () => {
        delete document.body.dataset.pageTheme;
      };
    }, []);

    useEffect(() => {
        fireAnalytics('pageload');
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <ConfigProvider
                {...process.env}
                {...window?.LEXUS?.config}
            >
                <FeatureFlagProvider>
                <PersonalizationProvider>
                    <ThemeProvider theme={theme}>
                        <Provider
                            store={store}
                        >
                        <ScrollPaddingTop />
                        <CMSData path="responsivegrid.:items.page_navigation" transform={transformHeader}>
                            <PageNavigation subnavTitle={<Icon src={logo} width="75px" alt="Logo" />} />
                        </CMSData>
                        <div className="body-copy">
                            <CopyBlock text={`<GRContent>${copy}</GRContent>`} />
                        </div>
                        <CMSData path="footer.footerMenu" transform={transformFooter}>
                            <GlobalFooter />
                        </CMSData>
                        <CMSData path="thirdPartyInterstitial.popUp">
                            <ThirdPartyInterstitial />
                        </CMSData>
                        </Provider>
                    </ThemeProvider>
                </PersonalizationProvider>
                </FeatureFlagProvider>
            </ConfigProvider>
        </QueryClientProvider>
    );
};

export default withAnalytics(App)({
    _component: 'App',
    app: "GR",
    siteSection: `${process.env.REACT_APP_ERROR_CODE} page`,
    errorCode: process.env.REACT_APP_ERROR_CODE,
    errorContent: copy.replace(/<\/h2><p>/g, '. ').replace(/<h2>|<\/p>/g, ''),
});
